import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { laptopSystemKeys } from "@/lib/query";

export type LaptopSystemForUser = {
  id: string;
  name: string;
  url: string;
  color: string;
  description: string | null;
  isGlobal: boolean;
};

export type LaptopSystemAdmin = {
  id: string;
  name: string;
  url: string;
  color: string;
  description: string | null;
  sortOrder: number;
  isGlobal: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  assignedUserIds: string[];
  assignedUsers: Array<{ id: string; name: string; email: string }>;
};

async function parseError(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === "object" && "error" in body) {
      const err = (body as { error: unknown }).error;
      if (typeof err === "string") return err;
    }
  } catch {
    // ignore
  }
  return res.statusText || "Request failed";
}

export function useMyLaptopSystems(enabled = true) {
  return useQuery({
    queryKey: laptopSystemKeys.me(),
    enabled,
    queryFn: async (): Promise<LaptopSystemForUser[]> => {
      const res = await fetch("/api/me/laptop-systems", { credentials: "include" });
      if (!res.ok) throw new Error(await parseError(res));
      const body: unknown = await res.json();
      if (!body || typeof body !== "object" || !("data" in body)) return [];
      const data = (body as { data: unknown }).data;
      return Array.isArray(data) ? (data as LaptopSystemForUser[]) : [];
    },
  });
}

export function useAdminLaptopSystems() {
  return useQuery({
    queryKey: laptopSystemKeys.adminList(),
    queryFn: async (): Promise<LaptopSystemAdmin[]> => {
      const res = await fetch("/api/admin/laptop-systems", { credentials: "include" });
      if (!res.ok) throw new Error(await parseError(res));
      const body: unknown = await res.json();
      if (!body || typeof body !== "object" || !("data" in body)) return [];
      const data = (body as { data: unknown }).data;
      return Array.isArray(data) ? (data as LaptopSystemAdmin[]) : [];
    },
  });
}

export type LaptopSystemWriteInput = {
  name: string;
  url: string;
  color?: string;
  description?: string | null;
  sortOrder?: number;
  isGlobal?: boolean;
  active?: boolean;
  assignedUserIds?: string[];
};

export function useCreateLaptopSystem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: LaptopSystemWriteInput) => {
      const res = await fetch("/api/admin/laptop-systems", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laptopSystemKeys.all });
      toast.success("System created");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to create system");
    },
  });
}

export function useUpdateLaptopSystem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<LaptopSystemWriteInput>) => {
      const { id, ...patch } = input;
      const res = await fetch(`/api/admin/laptop-systems/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laptopSystemKeys.all });
      toast.success("System updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to update system");
    },
  });
}

export function useDeleteLaptopSystem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/laptop-systems/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laptopSystemKeys.all });
      toast.success("System deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to delete system");
    },
  });
}
